import React, { useState, useEffect } from 'react';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  getIsAppPublished,
  getIsAndroidPublished,
  fetchAppPublishedStatus,
} from '../config/appStore';
import ComingSoonToast from './ComingSoonToast';

type BadgePlatform = 'apple' | 'android';

interface AppStoreBadgesProps {
  layout?: 'horizontal' | 'vertical';
  size?: 'small' | 'medium' | 'large';
  /** Show the Apple App Store badge. Defaults to true. */
  showApple?: boolean;
  /** Show the Google Play badge. Defaults to false (preserves prior callers). */
  showAndroid?: boolean;
  /** Credential-free click hook (telemetry). Publication gating still applies. */
  onBadgeClick?: (platform: BadgePlatform) => void;
  /** Horizontal justification. Defaults to centered (prior callers unchanged). */
  align?: 'center' | 'start';
  className?: string;
}

const sizeClasses = {
  small: 'h-10',
  medium: 'h-12',
  large: 'h-14',
};

const sizeRem = {
  small: 2.5,
  medium: 3,
  large: 3.5,
};

// Official artwork aspect ratios (width/height) from the SVG sources:
// Apple viewBox width 119.66407 height 40; Google viewBox 0 0 238.96 70.87.
const ARTWORK_ASPECT: Record<BadgePlatform, number> = {
  apple: 119.66407 / 40,
  android: 238.96 / 70.87,
};

const badgeImg = (
  heightClass: string,
  heightRem: number,
  platform: BadgePlatform,
  src: string,
  alt: string
) => (
  <img
    src={src}
    alt={alt}
    style={{
      width: `${heightRem * ARTWORK_ASPECT[platform]}rem`,
      height: 'auto',
      maxWidth: '100%',
    }}
    onError={(e) => {
      const target = e.target as HTMLImageElement;
      target.style.display = 'none';
      const fallback = document.createElement('div');
      fallback.className = `${heightClass} px-6 flex items-center justify-center bg-black text-white rounded-lg font-semibold`;
      fallback.textContent = alt;
      target.parentNode?.appendChild(fallback);
    }}
  />
);

const AppStoreBadges: React.FC<AppStoreBadgesProps> = ({
  layout = 'horizontal',
  size = 'medium',
  showApple = true,
  showAndroid = false,
  onBadgeClick,
  align = 'center',
  className = '',
}) => {
  const [toastPlatform, setToastPlatform] = useState<BadgePlatform | null>(null);

  useEffect(() => {
    fetchAppPublishedStatus();
  }, []);

  const justify = align === 'start' ? 'justify-start' : 'justify-center';
  const containerClasses = layout === 'horizontal'
    ? `flex flex-wrap gap-4 items-center ${justify}`
    : `flex flex-col gap-4 ${align === 'start' ? 'items-start' : 'items-center'}`;

  const heightClass = sizeClasses[size];
  const heightRem = sizeRem[size];

  // Each badge gates independently on its own platform flag so Apple and
  // Android can be Live / Coming Soon at different times.
  const handleAppleClick = (e: React.MouseEvent) => {
    onBadgeClick?.('apple');
    if (!getIsAppPublished()) {
      e.preventDefault();
      setToastPlatform('apple');
    }
  };

  const handleAndroidClick = (e: React.MouseEvent) => {
    onBadgeClick?.('android');
    if (!getIsAndroidPublished()) {
      e.preventDefault();
      setToastPlatform('android');
    }
  };

  return (
    <div className={`${containerClasses} ${className}`}>
      {showApple && (
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block max-w-full min-w-0 transition-transform hover:scale-105 active:scale-95"
          aria-label="Download on the App Store"
          onClick={handleAppleClick}
        >
          {badgeImg(
            heightClass,
            heightRem,
            'apple',
            '/assets/app-store-badges/Black_lockup/SVG/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg',
            'Download on the App Store'
          )}
        </a>
      )}

      {showAndroid && (
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block max-w-full min-w-0 transition-transform hover:scale-105 active:scale-95"
          aria-label="Get it on Google Play"
          onClick={handleAndroidClick}
        >
          {badgeImg(heightClass, heightRem, 'android', '/assets/images/google-play-badge.svg', 'Get it on Google Play')}
        </a>
      )}

      {toastPlatform && (
        <ComingSoonToast
          platform={toastPlatform}
          onClose={() => setToastPlatform(null)}
        />
      )}
    </div>
  );
};

export default AppStoreBadges;
